// Cross-domain attribution contract with the platform app.
//
// The landing pages (trein-vertraging.nl, start.trein-vertraging.nl) and the app
// (app.trein-vertraging.nl) are different origins, so nothing the ad platforms
// set on arrival here reaches the app on its own. Two mechanisms carry it across,
// and they fail in different ways, which is why both exist:
//
//   1. AttributionForwarder stamps app-bound anchors with the params, so a click
//      in this session lands with them on the URL. Passing on a param the
//      visitor's own current URL already carries stores nothing on the device, so
//      that much is not consent-gated. RECALLING a click id we kept - from memory
//      or from the cookie - is gated on advertising consent, because that is us
//      reusing an identifier rather than forwarding their own navigation. UTMs are
//      never gated here. Lost if they leave and return by another route.
//   2. The durable `tvt_ft` cookie below, written on the shared parent domain and
//      read server-side by the platform's App\Http\Middleware\CaptureAttribution.
//      Survives 90 days and carries two things a query string cannot: the landing
//      URL (`l`) and the true external referrer (`r`). This one IS consent-gated
//      and host-gated - see FirstTouchCapture.
//
// Before this existed, start.trein-vertraging.nl left no trace at all: every CTA
// was hardcoded to app.trein-vertraging.nl/login?link_id=... with no click id, and
// no cookie was written. `user_signup_attributions.initial_landing_url` never once
// recorded the host, so paid traffic through that page could not be attributed.
//
// The shapes below mirror App\Services\Tracking\FirstTouchCookie. Both sides must
// agree; a key added here that the platform does not read is silently inert.

import { site } from "./site";

// Ad-platform click identifiers. These are the reason the OneTrust "targeting"
// category (C0004) exists, so they are stored ONLY with advertising consent - see
// hasAdvertisingConsent in lib/consent and the gate in FirstTouchCapture. Keeping
// them in their own list is what makes that gate expressible.
export const CLICK_ID_PARAMS = [
  "gclid",
  "wbraid",
  "gbraid",
  "fbclid",
  "ttclid",
] as const;

// Campaign labels. They describe the campaign, not the person, and carry no
// platform identifier, so they ride along with analytics consent (C0002).
export const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

// Exactly the keys FirstTouchCookie::ATTRIBUTION_KEYS accepts, in its order.
export const ATTRIBUTION_PARAMS = [
  ...CLICK_ID_PARAMS,
  ...UTM_PARAMS,
] as const;

const CLICK_IDS: ReadonlySet<string> = new Set(CLICK_ID_PARAMS);

/** Is this attribution key an ad-platform identifier (and so C0004 material)? */
export function isClickId(key: string): boolean {
  return CLICK_IDS.has(key);
}

export const FIRST_TOUCH_COOKIE = "tvt_ft";

// FirstTouchCookie::LIFETIME_MINUTES (90 days), expressed for `max-age`.
const FIRST_TOUCH_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

// FirstTouchCookie::OWN_ROOT_DOMAIN. The cookie is deliberately set on the parent
// domain so app.* reads what the landing hosts wrote.
const ROOT_DOMAIN = "trein-vertraging.nl";

// Only these hosts may write the shared parent-domain cookie. An opt-in list, not
// a domain-suffix check: every preview and staging deploy is also a subdomain of
// trein-vertraging.nl, so a suffix check would let new.* seed the production
// first-touch record for a real visitor's browser. That is the same contamination
// PostHogProvider's ANALYTICS_HOSTS exists to stop, except this cookie feeds
// user_signup_attributions rather than a dashboard, so a bad write corrupts
// business attribution rather than a chart.
const ATTRIBUTION_HOSTS: string[] = [
  site.hosts.apex,
  site.hosts.www,
  site.hosts.paidVariant,
];

// FirstTouchCookie::MAX_VALUE_LENGTH / MAX_URL_LENGTH / MAX_RAW_LENGTH. PHP
// measures strlen() in bytes and truncates values with mb_substr() by codepoint,
// so both units are mirrored exactly below.
const MAX_VALUE_LENGTH = 255;
const MAX_URL_LENGTH = 2048;
const MAX_RAW_LENGTH = 4096;

// Browsers drop a cookie whose whole name=value pair exceeds ~4KB. Ours can reach
// that before the decoded JSON does, because percent-encoding expands it.
const MAX_COOKIE_BYTES = 4096;

// FirstTouchCookie::EXCLUDED_REFERRER_HOSTS. These are OAuth round-trips, not a
// real external referrer.
const EXCLUDED_REFERRER_HOSTS = ["accounts.google.com", "appleid.apple.com"];

type FirstTouch = Record<string, string>;

// Attribution seen anywhere in this page session, held in memory. Without it, a
// visitor who browses to a second page before answering the consent banner loses
// their click id outright: by then the params are gone from the URL, and the
// cookie was never written because consent had not been given yet. Memory rather
// than sessionStorage on purpose - holding a value until the banner is answered
// must not itself write to the device.
let pendingAttribution: Record<string, string> = {};

/** Drop the in-memory record. Exported for tests; see clearFirstTouch. */
export function forgetAttribution(): void {
  pendingAttribution = {};
}

/**
 * Fold this URL's attribution into the in-memory record. Safe to call on any
 * host, before consent, and on every navigation: it stores nothing on the device.
 * First touch wins here too, so a later page cannot overwrite the entry point.
 */
export function rememberAttribution(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  for (const key of ATTRIBUTION_PARAMS) {
    if (pendingAttribution[key]) continue;
    const value = params.get(key);
    if (!value) continue;
    const clean = truncate(stripControlChars(value), MAX_VALUE_LENGTH);
    if (clean !== "") pendingAttribution[key] = clean;
  }
}

const utf8 = new TextEncoder();

function byteLength(value: string): number {
  return utf8.encode(value).length;
}

// Codepoint-safe, mirroring PHP's mb_substr. A plain slice() cuts UTF-16 code
// units and can split a surrogate pair, which then makes encodeURIComponent throw
// URIError and silently lose the whole write.
function truncate(value: string, max: number): string {
  const points = Array.from(value);
  return points.length <= max ? value : points.slice(0, max).join("");
}

// FirstTouchCookie::parse strips these before storing.
function stripControlChars(value: string): string {
  return value.replace(/[\u0000-\u001F\u007F]/g, "");
}

function isHttpUrl(value: string): boolean {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
}

function isParseableDate(value: string): boolean {
  return !Number.isNaN(Date.parse(value));
}

/** Mirror of FirstTouchCookie::isExcludedReferrer. Unparseable means excluded. */
export function isExcludedReferrer(url: string): boolean {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return true;
  }
  if (!host) return true;
  if (host === ROOT_DOMAIN || host.endsWith(`.${ROOT_DOMAIN}`)) return true;
  return EXCLUDED_REFERRER_HOSTS.includes(host);
}

/** May this host write the shared parent-domain cookie? Opt-in by design. */
export function isAttributionHost(): boolean {
  if (typeof window === "undefined") return false;
  return ATTRIBUTION_HOSTS.includes(window.location.hostname.toLowerCase());
}

// Parent domain on the allowlisted hosts, host-only anywhere else. The component
// gate already stops a non-allowlisted host writing at all; this keeps the write
// working under `next dev` on localhost and in jsdom, where a cookie scoped to a
// domain the page is not on is simply rejected.
function cookieDomain(): string | null {
  return isAttributionHost() ? `.${ROOT_DOMAIN}` : null;
}

// The scope an EXISTING cookie has to be rewritten in, which is a wider set of
// hosts than the ones allowed to create one: the record is parent-domain scoped,
// so every subdomain can read it and must be able to act on a withdrawal. Only
// used by stripStoredClickIds, which never creates a record.
function existingCookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  const underRoot = host === ROOT_DOMAIN || host.endsWith(`.${ROOT_DOMAIN}`);
  return underRoot ? `.${ROOT_DOMAIN}` : null;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split("; ")) {
    const eq = part.indexOf("=");
    if (eq > 0 && part.slice(0, eq) === name) return part.slice(eq + 1);
  }
  return null;
}

/**
 * Mirror of FirstTouchCookie::parse, including its per-field validation: `r`/`l`
 * must be http(s) URLs, `fs` must be a parseable date, and every attribution
 * value has control characters stripped and is capped at MAX_VALUE_LENGTH. The
 * cookie is unencrypted and JS-writable by design, so anything may be in it.
 */
export function readFirstTouch(): FirstTouch {
  const raw = readCookie(FIRST_TOUCH_COOKIE);
  if (!raw) return {};

  let text = raw;
  if (!text.startsWith("{")) {
    try {
      text = decodeURIComponent(text);
    } catch {
      return {};
    }
  }
  if (byteLength(text) > MAX_RAW_LENGTH) return {};

  let decoded: unknown;
  try {
    decoded = JSON.parse(text);
  } catch {
    return {};
  }
  if (typeof decoded !== "object" || decoded === null || Array.isArray(decoded)) {
    return {};
  }

  const source = decoded as Record<string, unknown>;
  const out: FirstTouch = {};

  const fs = source.fs;
  if (typeof fs === "string" && fs !== "" && isParseableDate(fs)) out.fs = fs;

  for (const key of ["r", "l"] as const) {
    const url = source[key];
    if (
      typeof url === "string" &&
      byteLength(url) <= MAX_URL_LENGTH &&
      isHttpUrl(url)
    ) {
      out[key] = url;
    }
  }

  for (const key of ATTRIBUTION_PARAMS) {
    const value = source[key];
    if (typeof value !== "string" || value === "") continue;
    const clean = stripControlChars(value);
    if (clean === "") continue;
    out[key] = truncate(clean, MAX_VALUE_LENGTH);
  }

  return out;
}

function writeFirstTouch(
  payload: Record<string, unknown>,
  domain: string | null = cookieDomain(),
): void {
  const json = JSON.stringify(payload);
  // PHP's parse() gates on strlen() in bytes and returns {} above the limit,
  // which would blank a good earlier touch. Leave the existing cookie alone.
  if (byteLength(json) > MAX_RAW_LENGTH) return;

  let encoded: string;
  try {
    encoded = encodeURIComponent(json);
  } catch {
    // Lone surrogate somewhere in the payload. truncate() should prevent this;
    // bail rather than throw out of a useEffect.
    return;
  }

  const cookie = [
    `${FIRST_TOUCH_COOKIE}=${encoded}`,
    "path=/",
    domain ? `domain=${domain}` : "",
    `max-age=${FIRST_TOUCH_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
    window.location.protocol === "https:" ? "Secure" : "",
  ]
    .filter(Boolean)
    .join("; ");

  // Percent-encoding expands the payload, so the encoded pair can breach the
  // browser's ~4KB ceiling while the decoded JSON is still under PHP's. A cookie
  // over the limit is dropped silently, taking the earlier good value with it.
  if (byteLength(`${FIRST_TOUCH_COOKIE}=${encoded}`) > MAX_COOKIE_BYTES) return;

  document.cookie = cookie;
}

// The payload fields that hold a whole URL, and so can carry a click id inside a
// query string rather than in a key of its own. `r` counts as much as `l`: a
// visitor can arrive from a partner page that was itself reached through an ad,
// making document.referrer something like https://partner.nl/deals?gclid=...
const URL_FIELDS = ["l", "r"] as const;

/**
 * Take every trace of an ad-platform click id out of a payload, in place. Both
 * kinds of hiding place: a key of its own, and the query string of a stored URL.
 * Returns whether anything was actually removed.
 */
function removeClickIds(payload: FirstTouch): boolean {
  let changed = false;
  for (const key of CLICK_ID_PARAMS) {
    if (payload[key] === undefined) continue;
    delete payload[key];
    changed = true;
  }
  for (const key of URL_FIELDS) {
    const value = payload[key];
    if (!value) continue;
    const clean = withoutClickIds(value);
    if (clean === value) continue;
    payload[key] = clean;
    changed = true;
  }
  return changed;
}

/**
 * Strip the click ids out of a cookie that already exists, from ANY host.
 *
 * Withdrawing advertising consent while keeping analytics leaves the record in
 * place, so unlike a full withdrawal there is nothing to delete - it has to be
 * rewritten. That has to work wherever the visitor happens to be when they open
 * the preference centre, because the cookie is parent-domain scoped and readable
 * on every subdomain, including ones not allowed to create it. (The earlier fix
 * for the full-withdrawal path is the same lesson: a host gate on the write is
 * not a host gate on the withdrawal.)
 *
 * Never creates a record: with no cookie, or nothing to strip, it writes nothing
 * at all, so a host outside ATTRIBUTION_HOSTS still cannot seed one.
 */
export function stripStoredClickIds(): void {
  if (typeof document === "undefined") return;

  const existing = readFirstTouch();
  if (Object.keys(existing).length === 0) return;

  const payload: FirstTouch = { ...existing };
  if (!removeClickIds(payload)) return;

  writeFirstTouch({ v: 1, ...payload }, existingCookieDomain());
}

/**
 * Remove the cookie. Used whenever analytics consent is not granted, which must
 * work from any host: the cookie is scoped to the parent domain, so it is present
 * on every subdomain including ones that are not allowed to write it. Both scopes
 * are cleared because a host-only delete does not match a parent-domain cookie.
 *
 * Deliberately does NOT touch pendingAttribution. This runs on every render where
 * consent is merely unanswered, not only on a real withdrawal, and OneTrust gives
 * us no way to tell those apart. Wiping the in-memory record here would erase it
 * microseconds after it was made and reinstate the exact data loss the memory
 * exists to prevent. Memory is transient, never leaves the tab, and only ever
 * reaches the cookie once consent is granted - so there is nothing to clean up.
 */
export function clearFirstTouch(): void {
  if (typeof document === "undefined") return;
  for (const domain of [null, `.${ROOT_DOMAIN}`]) {
    document.cookie = [
      `${FIRST_TOUCH_COOKIE}=`,
      "path=/",
      domain ? `domain=${domain}` : "",
      "max-age=0",
    ]
      .filter(Boolean)
      .join("; ");
  }
}

// The landing URL as the platform would record it. Deliberately drops the
// fragment: a fragment never reaches a server, so the platform's own
// $request->fullUrl() cannot contain one, and copying `#access_token=...` out of
// the address bar into a 90-day JS-readable cookie (and from there into
// user_signup_attributions.initial_landing_url) would durably persist a secret
// the server was never meant to see.
function landingUrl(): string {
  const { origin, pathname, search } = window.location;
  return truncate(`${origin}${pathname}${search}`, MAX_URL_LENGTH);
}

/**
 * The same URL with the ad-platform click ids taken out of its query string.
 *
 * Gating the top-level `gclid` key is not enough on its own: `l` and `r` keep a
 * whole query string each, so a visit to `/?gclid=abc` would leave the identifier
 * sitting inside the landing URL for 90 days and hand it to the platform as
 * initial_landing_url, however carefully the dedicated key was withheld. The
 * consent answer has to reach all of them, on the way in and on withdrawal.
 *
 * Returns the input unchanged if it will not parse - `l` is only ever built from
 * window.location, but readFirstTouch accepts whatever is in the cookie.
 */
function withoutClickIds(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  let changed = false;
  for (const key of CLICK_ID_PARAMS) {
    if (!parsed.searchParams.has(key)) continue;
    parsed.searchParams.delete(key);
    changed = true;
  }
  return changed ? truncate(parsed.toString(), MAX_URL_LENGTH) : url;
}

/**
 * Write or top up the first-touch cookie. First touch wins: an existing key is
 * never overwritten, because the earliest touch is the one that acquired the
 * user. Only writes when something actually changed, matching the platform's own
 * persistFirstTouchCookie.
 *
 * `advertising` is the OneTrust C0004 (targeting) answer and is required rather
 * than defaulted, so a new call site cannot silently store ad identifiers. When
 * it is false the click ids are neither written nor kept: a visitor who accepts
 * analytics and refuses advertising, or who later withdraws advertising in the
 * preference centre, must not be left with a 90-day gclid on their device.
 *
 * Callers remain responsible for the analytics-consent and host gates (see
 * FirstTouchCapture).
 */
export function captureFirstTouch({
  advertising,
  now = new Date(),
}: {
  advertising: boolean;
  now?: Date;
}): void {
  if (typeof document === "undefined") return;

  const existing = readFirstTouch();
  const payload: FirstTouch = { ...existing };
  let changed = false;

  // Withdrawal path. readFirstTouch has already normalised the cookie, so a key
  // present here really is stored on the device.
  if (!advertising && removeClickIds(payload)) changed = true;

  rememberAttribution();
  const params = new URLSearchParams(window.location.search);
  const writable = advertising ? ATTRIBUTION_PARAMS : UTM_PARAMS;
  for (const key of writable) {
    if (payload[key]) continue;
    // The URL first, then what we remembered earlier in this page session: by
    // the time consent is granted the entry URL may be several navigations ago.
    const value = params.get(key) || pendingAttribution[key];
    if (!value) continue;
    const clean = truncate(stripControlChars(value), MAX_VALUE_LENGTH);
    if (clean === "") continue;
    payload[key] = clean;
    changed = true;
  }

  if (!payload.fs) {
    payload.fs = now.toISOString();
    changed = true;
  }
  if (!payload.l) {
    payload.l = advertising ? landingUrl() : withoutClickIds(landingUrl());
    changed = true;
  }
  if (!payload.r) {
    const referrer = document.referrer;
    if (referrer && !isExcludedReferrer(referrer)) {
      const kept = truncate(referrer, MAX_URL_LENGTH);
      payload.r = advertising ? kept : withoutClickIds(kept);
      changed = true;
    }
  }

  if (!changed) return;
  writeFirstTouch({ v: 1, ...payload });
}

/**
 * The attribution params to stamp onto app-bound links: what is on this URL,
 * falling back to the stored first touch so a visitor who returns without params
 * still carries their original click id across.
 *
 * Without advertising consent the click ids fall back to nothing: only a value the
 * visitor's own current URL already carries is passed on, which is a hand-off of
 * what their click brought with it rather than a recall of something we kept. The
 * remembered and stored copies are exactly the "we kept it" case, so they are
 * withheld. UTMs are unaffected - they describe the campaign, not the person.
 */
export function attributionParams({
  advertising,
}: {
  advertising: boolean;
}): Record<string, string> {
  if (typeof window === "undefined") return {};

  rememberAttribution();
  const params = new URLSearchParams(window.location.search);
  const stored = readFirstTouch();
  const out: Record<string, string> = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const mayRecall = advertising || !isClickId(key);
    const value =
      params.get(key) || (mayRecall ? pendingAttribution[key] || stored[key] : "");
    if (!value) continue;
    const clean = truncate(stripControlChars(value), MAX_VALUE_LENGTH);
    if (clean !== "") out[key] = clean;
  }
  return out;
}
